import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'cpcs-reconnect';
import { compose } from 'redux';
import I from 'immutable';
import cx from 'classnames';
import AccountNav from 'pages/common/topNav';
import AddressForm from 'pages/billingPage/form/addressForm';
import CardForm from 'pages/billingPage/form/cardForm';
import { CouponFormUpgradePlan } from 'pages/common/couponForm';
import SubscriptionType from 'pages/common/subscriptionType';
import { addNotification as addNotificationAction, saveStripeCardAction } from 'domain/env/EnvActions';
import { subscriptionTypeSelector, navigationBlocked_sel } from 'domain/env/EnvModel';
import { extraFooterGap_sel } from 'domain/ui/UIModel';
import { locationHash_sel, query_sel } from 'domain/router/RouterModel';
import {
  setSelectedPlanTypeAction,
  upgradePlanAction,
} from 'pages/upgradeSubscriptionPage/upgradeSubscriptionActions';
import { SubscriptionCongratulation } from 'pages/billingPage/PlanDetails';
import PageContainer from 'pages/common/pageContainer';
import {
  selectedPlanTypeSelector,
  planTypesSel,
  selectedSubscriptionSel,
  submitInProgressSel,
  planTypesLoading_sel,
} from 'pages/upgradeSubscriptionPage/upgradeSubscriptionModel';
import {
  percentOffSelector,
  amountOffSelector,
} from 'pages/common/couponForm/couponFormModel';
import {
  SUBSCRIPTION_TYPE_TRIAL,
  SUBSCRIPTION_TYPE_MEMBER,
  SUBSCRIPTION_TYPE_CUSTOM,
  SUBSCRIPTION_TYPE_ENTERPRISE,
} from 'domain/const';

import injectSheet from 'lib/sheet';
import styles from './sheet.js';

class UpgradeSubscriptionSectionPure extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    planTypes: PropTypes.instanceOf(I.Collection),
    setSelectedPlanType: PropTypes.func.isRequired,
    selectedPlanType: PropTypes.string,
    percentOff: PropTypes.number,
    amountOff: PropTypes.number,
    upgradePlan: PropTypes.func.isRequired,
    submitInProgress: PropTypes.bool,
    selectedSubscription: PropTypes.instanceOf(I.Collection),
    addNotification: PropTypes.func.isRequired,
    modifier: PropTypes.string,
    allowSaveBillingInfo: PropTypes.bool,
    saveStripeCard: PropTypes.func.isRequired,
    showCongratulation: PropTypes.bool,
    planTypesLoading: PropTypes.bool,
    currentSubscriptionType: PropTypes.string,
    locationHash: PropTypes.string,
    query: PropTypes.shape({
      congratulation: PropTypes.string,
    }),
    navigationBlocked: PropTypes.bool,
  };

  state = {
    formsReinitialized: false,
  };

  unmounted = false;
  rootRef = null;

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.scrolledToBilling);
  }

  scrolledToBilling = null;

  componentDidUpdate() {
    if (this.scrolledToBilling) {
      return;
    }
    const { planTypes } = this.props;
    if (planTypes && !!planTypes.size) {
      this.scrolledToBilling = setTimeout(this.scrollToRoot, 500);
    }
  }

  componentDidMount() {
    setTimeout(() => this.safeSetState({ formsReinitialized: true }), 500);
  }

  scrollToRoot = () => {
    const { locationHash, query } = this.props;
    if (locationHash !== '#billing' && (!query || !query.congratulation) && !this.expired) return;
    if (this.rootRef && !this.unmounted) {
      const y = window.scrollY + this.rootRef.getBoundingClientRect().y - 50;
      window.scrollTo(0, y);
    }
  }

  safeSetState = (state, cb) => {
    if (this.unmounted) return;
    this.setState(state, cb);
  }

  setRootRef = (node) => this.rootRef = node;

  onPlanTypeClick = (v) => () => {
    this.props.setSelectedPlanType(v.get('shortName'));
  }

  onStripeCardToken = ({ autoSubmit, ...payload }) => {
    const { saveStripeCard, allowSaveBillingInfo } = this.props;
    if (!autoSubmit && allowSaveBillingInfo) {
      saveStripeCard(payload);
    }
  };

  upgradePlan = () => {
    const { submitInProgress, selectedPlanType, currentSubscriptionType, planTypes, addNotification } = this.props;
    if (selectedPlanType === currentSubscriptionType) return;
    if (submitInProgress) return;
    if (!selectedPlanType || !planTypes.find(v => v.get('shortName') === selectedPlanType)) {
      addNotification({ title: 'Select subscription plan', type: 'error' });
      return;
    }
    this.props.upgradePlan();
  };

  get priceNum() {
    const { selectedSubscription/*, percentOff, amountOff*/ } = this.props;
    /**
     * selectedSubscription: { subscriptionProratedValue: 1400, subscriptionValue: 1800 }
    **/
    // if (!selectedSubscription) return null;
    // if (percentOff) {
    //   return selectedSubscription.subscriptionProratedValue - selectedSubscription.subscriptionProratedValue * percentOff / 100;
    // }
    // if (amountOff) {
    //   return Math.max(0, selectedSubscription.subscriptionProratedValue - amountOff / 100);
    // }
    return selectedSubscription.subscriptionProratedValue;
  }

  get price() {
    const { classes, currentSubscriptionType, selectedPlanType, percentOff, amountOff, selectedSubscription } = this.props;
    if (!selectedSubscription || (!this.priceNum && !percentOff && !amountOff)) return null;
    if (selectedPlanType === currentSubscriptionType) return null;
    return <span
      className={classes.priceVal}
    >
      Total: ${this.priceNum}
    </span>;
  }

  get upgradeDisabled() {
    const { selectedPlanType, currentSubscriptionType, planTypesLoading, submitInProgress } = this.props;
    return submitInProgress || planTypesLoading || selectedPlanType === currentSubscriptionType;
  }

  get expired() {
    const { currentSubscriptionType, navigationBlocked } = this.props;
    return currentSubscriptionType === SUBSCRIPTION_TYPE_TRIAL && !!navigationBlocked;
  }

  render() {
    const {
      classes, planTypes, selectedPlanType, currentSubscriptionType,
      showCongratulation, modifier,
      allowSaveBillingInfo,
    } = this.props;
    const disableUpgradeBtnClass = this.upgradeDisabled ? 'disabled' : '';
    const IS_TRIAL = currentSubscriptionType === SUBSCRIPTION_TYPE_TRIAL;
    const IS_MEMBER = currentSubscriptionType === SUBSCRIPTION_TYPE_MEMBER;
    return (
      <div className={cx('PAGE_SECTION', 'UpgradeSubscriptionSection', modifier)} id="billing" ref={this.setRootRef}>
        {
          selectedPlanType === SUBSCRIPTION_TYPE_ENTERPRISE &&
            <div className={classes.enterpriseText}>
              You are currently on an Enterprise plan. If you have any questions,{' '}
              please get in touch with your account representative or email us at{' '}
              <a href="mailto:support@artbnk.com" className={classes.supportMailLink}>support@artbnk.com</a>
            </div>
        }
        {
          this.expired &&
            <React.Fragment>
              <div
                className={classes.trialTitle}
                children="Your trial has expired."
              />
              <div
                className={classes.trialDescription}
                children="Please select one of the plans below to access your ARTBnk account."
              />
            </React.Fragment>
        }

        {
          selectedPlanType !== SUBSCRIPTION_TYPE_ENTERPRISE &&
            <div className={cx(classes.content, modifier)}>
              <div className={cx(classes.column1, modifier)} data-name="card-form-container">
                {
                  this.state.formsReinitialized &&
                    <CardForm
                      onSubmit={this.onStripeCardToken}
                      autoSubmit
                      hideActionButtons={!allowSaveBillingInfo}
                      titleModifier="upgradeSubscriptionPage"
                    />
                }
                {
                  this.state.formsReinitialized &&
                    <AddressForm
                      hideActionButtons={!allowSaveBillingInfo}
                      modifier="upgradeSubscriptionPage"
                      fixDropdownPadding
                    />
                }
                {
                  this.state.formsReinitialized &&
                    <CouponFormUpgradePlan
                      opened
                      modifier="upgrade-subscription"
                      cancelText="Clear"
                      required={false}
                    />
                }
              </div>
              <div className={cx(classes.column2, 'ChoosePlanForm', modifier)}>
                {
                  currentSubscriptionType !== SUBSCRIPTION_TYPE_CUSTOM && currentSubscriptionType !== SUBSCRIPTION_TYPE_ENTERPRISE &&
                    <React.Fragment>
                      <h3
                        className={classes.formTitle}
                        children="Subscription Details"
                      />
                      {
                        showCongratulation &&
                          <SubscriptionCongratulation />
                      }
                      {
                        (IS_TRIAL || IS_MEMBER) &&
                          <p
                            className={classes.p}
                          >
                            Enjoy additional features and save on valuations by upgrading to one of our annual{' '}
                            <a
                              href="https://www.artbnk.com/pricing"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.link}
                              children="plans"
                            />.
                          </p>
                      }
                      {
                        planTypes && !!planTypes.size && planTypes.map(v => (
                          <SubscriptionType
                            key={v.get('shortName')}
                            onClick={this.onPlanTypeClick(v)}
                            selected={selectedPlanType ? v.get('shortName') === selectedPlanType : v.get('shortName') === currentSubscriptionType}
                            disabled={v.get('shortName') === currentSubscriptionType}
                            item={v}
                            modifier={modifier}
                          />
                        ))
                      }
                      <div
                        className={classes.actions}
                      >
                        <div
                          className={classes.priceWrap}
                          children={this.price}
                        />
                        <button
                          type="button"
                          onClick={this.upgradePlan}
                          className={cx(classes.upgradePlanBtn, disableUpgradeBtnClass)}
                          disabled={this.upgradeDisabled}
                          children="upgrade my plan"
                        />
                      </div>
                    </React.Fragment>
                }
              </div>
            </div>
        }
        {
          selectedPlanType !== SUBSCRIPTION_TYPE_ENTERPRISE &&
            <div className={cx(classes.content, modifier)}>
              <div className={cx(classes.column1, modifier)}>
                <div className={classes.helpMessage}>
                  Please visit the{' '}
                  <a
                    className={classes.link}
                    href="https://support.artbnk.com/hc"
                    target="_blank"
                    rel="noreferrer noopener"
                    children="Help Center"
                  />
                  {' '}
                  to get in touch with our customer service team if you have any questions.
                </div>
              </div>
            </div>
        }
      </div>
    );
  }
}

export const UpgradeSubscriptionSection = compose(
  injectSheet(styles),
  connect({
    planTypes: planTypesSel,
    setSelectedPlanType: setSelectedPlanTypeAction,
    selectedPlanType: selectedPlanTypeSelector,
    selectedSubscription: selectedSubscriptionSel,
    percentOff: percentOffSelector,
    amountOff: amountOffSelector,
    upgradePlan: upgradePlanAction,
    submitInProgress: submitInProgressSel,
    addNotification: addNotificationAction,
    saveStripeCard: saveStripeCardAction,
    currentSubscriptionType: subscriptionTypeSelector,
    query: query_sel,
    locationHash: locationHash_sel,
    planTypesLoading: planTypesLoading_sel,
    navigationBlocked: navigationBlocked_sel,
  }),
)(UpgradeSubscriptionSectionPure);

function UpgradeSubscriptionPage({ classes, extraFooterGap, match: { path } }) {
  return (
    <PageContainer modifier={cx('UpgradeSubscriptionPage', { extraFooterGap })}>
      <div className="PAGE_SECTION UpgradeSubscriptionPage">
        <div className={classes.header}>
          <AccountNav path={path} />
        </div>
        <UpgradeSubscriptionSection modifier="UpgradeSubscriptionPage" />
      </div>
    </PageContainer>
  );
}

UpgradeSubscriptionPage.propTypes = {
  extraFooterGap: PropTypes.bool.isRequired,
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    header: PropTypes.string.isRequired,
  }).isRequired,
};

export default compose(
  injectSheet(styles),
  connect({
    extraFooterGap: extraFooterGap_sel,
  }),
)(UpgradeSubscriptionPage);

